import { getDepots } from "@spark/services/depots";
import { getAcompanhamento, createAcompanhamento, updateAcompanhamento } from "@spark/services/acompanhamentos";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { formStructure, formSections } from './formConfig';
import { notification } from '@spark/ui-components/utils/notification';
import { ButtonWithLoading, Button } from '@spark/ui-components/components/Button';
import { Form } from 'react-final-form';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';

const AcompanhamentoForm = ({
  acompanhamentoId,
  isReadOnly = false,
  hideHeader = false
}) => {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const isEditing = !!id || !!acompanhamentoId;
  const entityId = id || acompanhamentoId;

  const loadAcompanhamento = useCallback(async () => {
    try {
      const response = await getAcompanhamento(entityId);
      const data = response.data || response;

      if (data) {
        const mappedData = {
          titulo: data.Titulo || '',
          descricao: data.Descricao || '',
          depotId: data.DepotId
            ? {
                value: data.DepotId,
                label:
                  data.NumeroContainer ||
                  data.Depot?.NumeroContainer ||
                  data.DepotNumeroContainer ||
                  data.DepotNumero ||
                  `Depot ${data.DepotId}`,
              }
            : null,
          acompanhamentoInterno: data.AcompanhamentoInterno === 1 || data.AcompanhamentoInterno === true,
        };
        setInitialData(mappedData);
      }
    } catch (error) {
      console.error('Erro ao carregar acompanhamento:', error);
      notification.error('Erro ao carregar dados do acompanhamento');
    }
  }, [entityId]);

  useEffect(() => {
    let isMounted = true;
    if (isEditing && entityId) {
      setFormLoading(true);
      loadAcompanhamento().finally(() => {
        if (isMounted) {
          setFormLoading(false);
        }
      });
    }
    return () => {
      isMounted = false;
    };
  }, [isEditing, entityId, loadAcompanhamento]);

  const initialValues = useMemo(() => {
    if (!initialData) {
      return {
        titulo: '',
        descricao: '',
        depotId: null,
        acompanhamentoInterno: true,
      };
    }
    return initialData;
  }, [initialData]);

  const handleSearchDepots = useCallback(async (searchTerm) => {
    try {
      const response = await getDepots({
        NumeroContainer: searchTerm || undefined,
        quantidadePorPagina: 10,
        pagina: 1,
      });
      const registros = response?.registros || [];
      return registros.map((depot) => ({
        value: depot.Id,
        label: depot.NumeroContainer || `Depot ${depot.Id}`,
      }));
    } catch (error) {
      console.error('Erro ao buscar depots:', error);
      return [];
    }
  }, []);

  const formStructureWithSearch = useMemo(() => {
    return formStructure.map((row) =>
      row.map((field) => {
        const updatedField = { ...field };
        if (field.name === 'depotId') {
          updatedField.onSearch = handleSearchDepots;
        }
        if (isReadOnly) {
          updatedField.disabled = true;
        }
        return updatedField;
      })
    );
  }, [handleSearchDepots, isReadOnly]);

  const onSubmit = useCallback(
    async (values) => {
      if (isReadOnly) return;

      setLoading(true);

      try {
        const payload = {
          titulo: values.titulo,
          descricao: values.descricao,
          cargaId: null,
          depotId: values.depotId?.value || null,
          acompanhamentoInterno: values.acompanhamentoInterno || false,
        };

        if (isEditing) {
          await updateAcompanhamento(entityId, payload);
          notification.success('Acompanhamento atualizado com sucesso!');
        } else {
          await createAcompanhamento(payload);
          notification.success('Acompanhamento criado com sucesso!');
        }

        history.goBack();
      } catch (error) {
        console.error('Erro ao salvar acompanhamento:', error);
        notification.error('Erro ao salvar acompanhamento');
      } finally {
        setLoading(false);
      }
    },
    [history, entityId, isEditing, isReadOnly]
  );

  const handleCancel = useCallback(() => {
    history.goBack();
  }, [history]);

  if (formLoading) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '50px',
        }}
      >
        Carregando dados do acompanhamento...
      </div>
    );
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      key={`${entityId}-${JSON.stringify(initialData)}`}
    >
      {({ handleSubmit }) => (
        <FormContainer onSubmit={handleSubmit}>
          <DynamicForm
            structure={formStructureWithSearch}
            sections={formSections(initialValues)}
            isViewMode={isReadOnly}
          />

          {!isReadOnly && (
            <FormButtonToolbar>
              <ButtonWithLoading
                variant="primary"
                type="submit"
                loading={loading ? true : undefined}
              >
                {isEditing ? 'Atualizar' : 'Criar'}
              </ButtonWithLoading>
              <Button variant="secondary" type="button" onClick={handleCancel}>
                Cancelar
              </Button>
            </FormButtonToolbar>
          )}
        </FormContainer>
      )}
    </Form>
  );
};

export default AcompanhamentoForm;
